import React, { useEffect, useState } from 'react'
import { XIcon, PhoneIcon, CalendarIcon, UsersIcon, StickyNoteIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchJobTimeEntries } from '../../api/jobRelatedRecords'
import { fetchCrewsAndEmployees, assignJobCrew, assignJobEmployee } from '../../api/jobWorkflow'
import { getStatusColor, formatStatusLabel } from '../../lib/jobStatusWorkflow'
import { JobTaskChecklist } from '../job/JobTaskChecklist'
import { JobGallery } from '../job/JobGallery'
import { DayJobAssignPanel } from '../job/DayJobAssignPanel'
import type { EndOfDayJob } from '../../api/endOfDay'
import { ViewTabBar } from '../ViewPageLayout'

interface EndOfDayJobPanelProps {
  job: EndOfDayJob
  canAct: boolean
  canSeePricing: boolean
  onClose: () => void
  onUpdated: () => void
}

export function EndOfDayJobPanel({
  job,
  canAct,
  canSeePricing,
  onClose,
  onUpdated,
}: EndOfDayJobPanelProps) {
  const [activeTab, setActiveTab] = useState<'checklist' | 'photos' | 'time' | 'notes'>('checklist')
  const [timeEntries, setTimeEntries] = useState<Awaited<ReturnType<typeof fetchJobTimeEntries>>>([])
  const [noteText, setNoteText] = useState(job.completion_notes || job.notes || '')
  const [rescheduleDate, setRescheduleDate] = useState(job.scheduled_date || '')
  const [assignments, setAssignments] = useState<any[]>([])
  const [crews, setCrews] = useState<any[]>([])
  const [employees, setEmployees] = useState<any[]>([])
  const [assignEditing, setAssignEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchJobTimeEntries(job.id).then(setTimeEntries).catch(() => setTimeEntries([]))
    supabase
      .from('job_assignments')
      .select('id, crew_id, employee_id, crew:crews(id, name), employee:employees(id, first_name, last_name, employee_number)')
      .eq('job_id', job.id)
      .is('unassigned_at', null)
      .then(({ data }) => setAssignments(data || []))
    fetchCrewsAndEmployees().then(({ crews: c, employees: e }) => {
      setCrews(c)
      setEmployees(e)
    })
  }, [job.id])

  const saveNote = async () => {
    setSaving(true)
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ completion_notes: noteText.trim() || null })
        .eq('id', job.id)
      if (error) throw error
      onUpdated()
    } finally {
      setSaving(false)
    }
  }

  const saveReschedule = async () => {
    if (!rescheduleDate) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ scheduled_date: rescheduleDate })
        .eq('id', job.id)
      if (error) throw error
      onUpdated()
    } finally {
      setSaving(false)
    }
  }

  const handleAssignCrew = async (crewId: string) => {
    setSaving(true)
    try {
      await assignJobCrew(job.id, crewId)
      onUpdated()
      const { data } = await supabase
        .from('job_assignments')
        .select('id, crew_id, employee_id, crew:crews(id, name), employee:employees(id, first_name, last_name, employee_number)')
        .eq('job_id', job.id)
        .is('unassigned_at', null)
      setAssignments(data || [])
    } finally {
      setSaving(false)
    }
  }

  const handleAssignEmployee = async (employeeId: string) => {
    setSaving(true)
    try {
      await assignJobEmployee(job.id, employeeId)
      onUpdated()
      const { data } = await supabase
        .from('job_assignments')
        .select('id, crew_id, employee_id, crew:crews(id, name), employee:employees(id, first_name, last_name, employee_number)')
        .eq('job_id', job.id)
        .is('unassigned_at', null)
      setAssignments(data || [])
    } finally {
      setSaving(false)
    }
  }

  const checklistPct =
    job.checklistTotal > 0 ? Math.round((job.checklistDone / job.checklistTotal) * 100) : null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-lg bg-white shadow-xl h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">{job.job_number ? `#${job.job_number}` : 'Job'}</p>
            <h2 className="text-lg font-semibold text-gray-900 truncate">{job.title}</h2>
            <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(job.status)}`}>
              {formatStatusLabel(job.status)}
            </span>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-600 space-y-1">
          <div>{job.customer?.company_name || '—'}</div>
          <div>{job.property?.address || '—'}</div>
          {job.techs.length > 0 && <div>Crew: {job.techs.join(', ')}</div>}
          {checklistPct != null && <div>Checklist: {checklistPct}% · Photos: {job.photos}</div>}
          {canSeePricing && (
            <div>
              Quoted: ${job.daily_quoted_revenue.toFixed(2)} · Actual: ${job.daily_actual_revenue.toFixed(2)}
            </div>
          )}
        </div>

        {canAct && (
          <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-2">
            {job.customer?.phone && (
              <a
                href={`tel:${job.customer.phone}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                <PhoneIcon className="w-4 h-4" />
                Call
              </a>
            )}
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              <StickyNoteIcon className="w-4 h-4" />
              Note
            </button>
            <button
              type="button"
              onClick={() => setAssignEditing((v) => !v)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              <UsersIcon className="w-4 h-4" />
              Reassign
            </button>
            <div className="inline-flex items-center gap-1">
              <CalendarIcon className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                className="text-sm border border-gray-200 rounded px-2 py-1"
              />
              <button
                type="button"
                disabled={saving}
                onClick={saveReschedule}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Reschedule
              </button>
            </div>
          </div>
        )}

        {assignEditing && canAct && (
          <div className="px-4 py-3 border-b border-gray-100">
            <DayJobAssignPanel
              assignments={assignments}
              crews={crews}
              employees={employees}
              isEditing={true}
              saving={saving}
              onToggleEdit={() => setAssignEditing(false)}
              onAssignCrew={handleAssignCrew}
              onAssignEmployee={handleAssignEmployee}
              onRemove={() => {}}
              compact
            />
          </div>
        )}

        <ViewTabBar
          tabs={[
            { id: 'checklist', label: 'Checklist' },
            { id: 'photos', label: 'Photos' },
            { id: 'time', label: 'Time' },
            { id: 'notes', label: 'Notes' },
          ]}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as typeof activeTab)}
          className="px-4"
        />

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'checklist' && <JobTaskChecklist jobId={job.id} />}
          {activeTab === 'photos' && <JobGallery jobId={job.id} />}
          {activeTab === 'time' && (
            <div className="space-y-2 text-sm">
              {timeEntries.length === 0 ? (
                <p className="text-gray-500">No time entries.</p>
              ) : (
                timeEntries.map((e) => (
                  <div key={e.id} className="border border-gray-100 rounded-lg p-2">
                    <div className="font-medium">
                      {e.employee?.first_name} {e.employee?.last_name}
                    </div>
                    <div className="text-gray-600">
                      {new Date(e.clock_in_time).toLocaleString()}
                      {e.clock_out_time ? ` – ${new Date(e.clock_out_time).toLocaleString()}` : ' (open)'}
                    </div>
                    {e.total_hours != null && <div>{Number(e.total_hours).toFixed(2)} hrs</div>}
                  </div>
                ))
              )}
            </div>
          )}
          {activeTab === 'notes' && (
            <div className="space-y-3">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={5}
                disabled={!canAct}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Office / completion notes"
              />
              {canAct && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Save note
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
